// Libraries
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  IconButton,
  Button,
  Typography,
  Box,
  MenuItem,
  Tooltip,
  InputAdornment,
  Menu,
  List,
  ListItem,
  Checkbox,
  TextField as MuiTextField,
  Paper,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Grid,
  FormControl,
  InputLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleOutlinedIcon from '@mui/icons-material/DragHandleOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CategoryIcon from '@mui/icons-material/Category';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HourglassFullIcon from '@mui/icons-material/HourglassFull';
import InventoryIcon from '@mui/icons-material/Inventory';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import TaskIcon from '@mui/icons-material/Task';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  RemoveCircleOutline,
  SettingsSuggest,
  VisibilityOff,
} from '@mui/icons-material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import NumbersIcon from '@mui/icons-material/Numbers';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImageIcon from '@mui/icons-material/Image';
import CollectionsIcon from '@mui/icons-material/Collections';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ViewListIcon from '@mui/icons-material/ViewList';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import FlagIcon from '@mui/icons-material/Flag';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import DrawIcon from '@mui/icons-material/Draw';
import DescriptionIcon from '@mui/icons-material/Description';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  CheckCircle,
  Error,
  Star,
  Favorite,
  Flag,
  LocalOffer,
  AttachMoney,
} from '@mui/icons-material';

// Components
import TextField from '../../stories/general-components/TextField';
import Select from '../../stories/general-components/Select';
import GeneralText from '../../stories/general-components/GeneralText';
import IconUploader from './IconUploader';
import Selection from '../../stories/general-components/Selection';
import { useSelector } from 'react-redux';
import Blocks from '../../stories/layout-components/Block';

const FieldEditor = ({ fields, setFields, fromExtensions, isExternal }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [displayRegexMenu, setDisplayRegexMenu] = useState(false);
  const [currentRegexIndex, setCurrentRegexIndex] = useState(null);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [typeScrollIndex, setTypeScrollIndex] = useState(0);
  const typesPerPage = 8;

  const businessPreference = useSelector((state) => state.core.businessData);
  const groupsList = businessPreference?.groups?.map((group) => group?.identifiant);

  // Add useEffect to initialize typeScrollIndex when field changes
  useEffect(() => {
    if (selectedFieldIndex !== null && fields[selectedFieldIndex]) {
      const currentType = fields[selectedFieldIndex].typeData;
      const typeIndex = fieldTypes.findIndex(type => type.value === currentType);
      if (typeIndex !== -1) {
        setTypeScrollIndex(Math.floor(typeIndex / typesPerPage));
      }
    }
  }, [selectedFieldIndex, fields]);

  // Optimized useCallback hooks for event handlers
  const handleFieldChange = useCallback((index, key, value) => {
    setFields(
      fields?.map((field, idx) =>
        idx === index ? { ...field, [key]: value } : field
      )
    );
  }, [fields, setFields]);

  const handleGroupsChange = useCallback((index, groups) => {
    setFields(
      fields?.map((field, idx) =>
        idx === index ? { ...field, groups } : field
      )
    );
  }, [fields, setFields]);

  const handleVisibilityChange = useCallback((index, visibility) => {
    setFields(
      fields?.map((field, idx) =>
        idx === index ? { ...field, visibility } : field
      )
    );
  }, [fields, setFields]);

  const handleConditionsChange = useCallback((index, conditions) => {
    setFields(
      fields?.map((field, idx) =>
        idx === index ? { ...field, conditional: conditions } : field
      )
    );
  }, [fields, setFields]);

  const addCondition = useCallback(() => {
    if (selectedFieldIndex !== null && selectedFieldIndex !== undefined) {
      const updatedConditions = [
        ...(fields[selectedFieldIndex]?.conditional || []),
        { field: '', operator: '', value: '', logic: 'AND' },
      ];
      handleConditionsChange(selectedFieldIndex, updatedConditions);
    }
  }, [fields, handleConditionsChange, selectedFieldIndex]);

  const removeCondition = useCallback((index) => {
    if (selectedFieldIndex !== null && selectedFieldIndex !== undefined) {
      const updatedConditions = fields[selectedFieldIndex]?.conditional?.filter((_, idx) => idx !== index) || [];
      handleConditionsChange(selectedFieldIndex, updatedConditions);
    }
  }, [fields, handleConditionsChange, selectedFieldIndex]);

  const handleConditionChange = useCallback((index, key, value) => {
    if (selectedFieldIndex !== null && selectedFieldIndex !== undefined) {
      const updatedConditions = fields[selectedFieldIndex]?.conditional?.map((condition, idx) =>
        idx === index ? { ...condition, [key]: value } : condition
      );
      handleConditionsChange(selectedFieldIndex, updatedConditions);
    }
  }, [fields, handleConditionsChange, selectedFieldIndex]);

  const addField = useCallback(() => {
    setFields([
      ...fields,
      {
        id: uuidv4(),
        name: '',
        label: '',
        value: '',
        validation: '',
        typeData: 'string',
        defaultValue: '',
        type: 'data',
        selections: [],
        conditional: [],
        public: false,
        visibility: 'both',
        groups: [],
        suffix: '',
        prefix: '',
        moreDetails: [],
      },
    ]);
  }, [fields, setFields]);

  const removeField = useCallback((index) => {
    setFields(fields.filter((_, idx) => idx !== index));
  }, [fields, setFields]);

  const publicField = useCallback((index) => {
    setFields(
      fields?.map((field, idx) =>
        idx === index ? { ...field, public: !field?.public } : field
      )
    );
  }, [fields, setFields]);

  const setRequired = useCallback((index, value) => {
    setFields(
      fields?.map((field, idx) =>
        idx === index ? { ...field, required: value } : field
      )
    );
  }, [fields, setFields]);

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const items = Array.from(fields);
    const [reorderedItem] = items?.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFields(items);
  }, [fields, setFields]);

  const addSelectionToField = useCallback((fieldIndex) => {
    if (fieldIndex !== null && fieldIndex !== undefined) {
      const updatedFields = fields?.map((field, idx) => {
        if (idx === fieldIndex) {
          const newSelection = {
            id: uuidv4(),
            label_fr: '',
            label_en: '',
            value: field?.selections?.length,
            color: '#696969',
            icon: 'Info',
            media_url: '',
            numericValue: 0,
          };
          const updatedSelections = [...(field.selections || []), newSelection];
          return { ...field, selections: updatedSelections };
        }
        return field;
      });

      setFields(updatedFields);
    }
  }, [fields, setFields]);

  const removeSelectionField = useCallback((fieldIndex) => {
    if (fieldIndex !== null && fieldIndex !== undefined) {
      const updatedFields = fields?.map((field, idx) => {
        if (idx === fieldIndex) {
          const updatedSelections = field?.selections?.slice(0, -1) || [];
          return { ...field, selections: updatedSelections };
        }
        return field;
      });
      setFields(updatedFields);
    }
  }, [fields, setFields]);

  const handleSelectionChange = useCallback((fieldIndex, selectionIndex, key, value) => {
    if (fieldIndex !== null && fieldIndex !== undefined && selectionIndex !== null && selectionIndex !== undefined) {
      const updatedFields = fields?.map((field, idx) => {
        if (idx === fieldIndex) {
          const updatedSelections = field?.selections?.map((sel, selIdx) => {
            if (selIdx === selectionIndex) {
              return { ...sel, [key]: value };
            }
            return sel;
          });
          return { ...field, selections: updatedSelections };
        }
        return field;
      });
      setFields(updatedFields);
    }
  }, [fields, setFields]);

  const addMedia = useCallback((mediaUrl, fieldIndex, selectionIndex) => {
    if (fieldIndex !== null && fieldIndex !== undefined && selectionIndex !== null && selectionIndex !== undefined) {
      const updatedFields = fields?.map((field, idx) => {
        if (idx === fieldIndex) {
          const updatedSelections = field?.selections?.map((sel, selIdx) => {
            return selIdx === selectionIndex ? { ...sel, media_url: mediaUrl?.[0]?.fileUrl } : sel;
          });
          return { ...field, selections: updatedSelections };
        }
        return field;
      });
      setFields(updatedFields);
    }
  }, [fields, setFields]);

  const handleDisplayRegex = useCallback((index) => {
    setDisplayRegexMenu(!displayRegexMenu);
    setCurrentRegexIndex(index);
  }, [setDisplayRegexMenu, setCurrentRegexIndex]);

  const handleSelectRegex = useCallback((index, value) => {
    handleFieldChange(index, 'validation', value);
    setDisplayRegexMenu(false);
  }, [handleFieldChange, setDisplayRegexMenu]);

  const handleFieldSelect = useCallback((index) => {
    setSelectedFieldIndex(index);
  }, [setSelectedFieldIndex]);

  const fieldTypes = [
    { value: 'string', label: t('text'), icon: TextFieldsIcon, fields: ['help', 'transform', 'suffix', 'prefix', 'expression', 'visibility', 'groups', 'conditions'] },
    { value: 'email', label: t('email'), icon: EmailIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'phone', label: t('phone'), icon: PhoneIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'number', label: t('number'), icon: NumbersIcon, fields: ['help', 'suffix', 'prefix', 'visibility', 'groups', 'conditions'] },
    { value: 'boolean', label: t('boolean'), icon: ToggleOnIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'date', label: t('date'), icon: CalendarTodayIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'date-time', label: t('dateTime'), icon: AccessTimeIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'media', label: t('file'), icon: CollectionsIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'media-single', label: t('fileSingle'), icon: ImageIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'media-single-private', label: t('fileSinglePrivate'), icon: ImageIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'geo', label: t('location'), icon: LocationOnIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'selection', label: t('selection'), icon: ViewListIcon, fields: ['help', 'visibility', 'groups', 'conditions', 'selections'] },
    { value: 'multi-select', label: t('multiSelect'), icon: CheckBoxIcon, fields: ['help', 'visibility', 'groups', 'conditions', 'selections'] },
    { value: 'dropdown', label: t('dropdown'), icon: ArrowDropDownCircleIcon, fields: ['help', 'visibility', 'groups', 'conditions', 'selections'] },
    { value: 'status', label: t('status'), icon: FlagIcon, fields: ['help', 'visibility', 'groups', 'conditions', 'selections'] },
    { value: 'slider', label: t('slider'), icon: LinearScaleIcon, fields: ['help', 'visibility', 'groups', 'conditions', 'selections'] },
    { value: 'list', label: t('list'), icon: FormatListBulletedIcon, fields: ['help', 'visibility', 'groups', 'conditions', 'selections'] },
    { value: 'signature', label: t('signature'), icon: DrawIcon, fields: ['help', 'visibility', 'groups', 'conditions'] },
    { value: 'page', label: t('page'), icon: DescriptionIcon, fields: ['help', 'conditions'] },
    { value: 'section', label: t('section'), icon: ViewModuleIcon, fields: ['help', 'conditions'] },
  ];

  const handleTypeScroll = (direction) => {
    if (direction === 'left') {
      setTypeScrollIndex(Math.max(0, typeScrollIndex - 1));
    } else {
      setTypeScrollIndex(Math.min(
        Math.ceil(fieldTypes.length / typesPerPage) - 1,
        typeScrollIndex + 1
      ));
    }
  };

  const getCurrentFieldType = useCallback((typeData) => {
    return fieldTypes.find(type => type.value === typeData) || fieldTypes[0];
  }, []);

  const renderSelectionManagement = useCallback((field, fieldIndex) => {
    const selections = Array.isArray(field?.selections)
      ? field?.selections
      : [];

    return (
      <>
        {selections?.map((selection, selectionIndex) => {
          if (typeof selection === 'string') {
            return null;
          } else {
            return (
              <ListItem sx={{ borderBottom: '1px solid #E0E0E0' }} dense key={selectionIndex + 'select'}>
                <div
                  style={{ width: '100%' }}
                  className="d-flex middle-content"
                >
                  <div className={'col-4'}>
                    <MuiTextField
                      variant="standard"
                      sx={{
                        mt: 0.8,
                      }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                      value={selection?.label || selection?.['label_' + currentLang]}
                      onChange={(e) =>
                        handleSelectionChange(
                          fieldIndex,
                          selectionIndex,
                          'label',
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </div>
                  <div className={'col-2'}>
                    <MuiTextField
                      type="color"
                      variant="standard"
                      sx={{
                        mt: 0.8,
                        '& .MuiInputBase-root': {
                          height: '20px',
                          width: '20px',
                          padding: 0,
                          borderRadius: '100px',
                          border: '1px solid',
                          borderColor: 'divider',
                          overflow: 'hidden',
                          '&:hover': {
                            borderColor: 'primary.main',
                          },
                          '&:focus-within': {
                            borderColor: 'primary.main',
                            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                          }
                        },
                        '& .MuiInputBase-input': {
                          padding: 0,
                          cursor: 'pointer',
                          '&::-webkit-color-swatch': {
                            border: 'none',
                            borderRadius: '100px',
                            padding: 0,
                          },
                          '&::-webkit-color-swatch-wrapper': {
                            border: 'none',
                            borderRadius: '100px',
                            padding: 0,
                          }
                        }
                      }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                      value={selection?.color}
                      onChange={(e) =>
                        handleSelectionChange(
                          fieldIndex,
                          selectionIndex,
                          'color',
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </div>
                  {(field?.typeData === 'select' ||
                    field?.typeData === 'selection' ||
                    field?.typeData === 'status') && (
                      <div className={'col-3'}>
                        <MuiTextField
                          select
                          sx={{
                            px: 3,
                            textAlign: 'center',
                            maxHeight: '25px',
                            minHeight: '25px',
                          }}
                          InputProps={{
                            disableUnderline: true,
                          }}
                          variant="standard"
                          value={selection?.icon}
                          onChange={(e) =>
                            handleSelectionChange(
                              fieldIndex,
                              selectionIndex,
                              'icon',
                              e.target.value
                            )
                          }

                        >
                          <MenuItem
                            value="AssignmentInd"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <AssignmentIndIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Task"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <TaskIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Category"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <CategoryIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Cancel"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <CancelIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Inventory"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <InventoryIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="ContactSupport"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <ContactSupportIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="EventNote"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <EventNoteIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="ThumbUp"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <ThumbUpIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="ThumbDown"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <ThumbDownIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="HourglassFull"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <HourglassFullIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Lock"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <LockIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Warning"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <WarningIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Info"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <InfoIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Visibility"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <VisibilityIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Person"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <PersonIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="CheckCircle"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <CheckCircle
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Error"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <Error
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Star"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <Star
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Favorite"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <Favorite
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Flag"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <Flag
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="LocalOffer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <LocalOffer
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="AttachMoney"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <AttachMoney
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Email"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <EmailIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="Phone"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <PhoneIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                          <MenuItem
                            value="LocationOn"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '32px',
                              width: '100%',
                              padding: '4px'
                            }}
                          >
                            <LocationOnIcon
                              htmlColor={selection?.color}
                              sx={{ fontSize: '20px' }}
                            />
                          </MenuItem>
                        </MuiTextField>
                      </div>
                    )}
                  {field?.typeData === 'status' && (
                    <div className={'col-3'}>
                      <Tooltip title={t('considerIsDone')}>
                        <span>
                          <Checkbox
                            key={selectionIndex + 'isDone'}
                            checked={selection?.isDone}
                            onClick={(e) =>
                              handleSelectionChange(
                                fieldIndex,
                                selectionIndex,
                                'isDone',
                                !selection?.isDone
                              )
                            }
                          />
                        </span>
                      </Tooltip>
                    </div>
                  )}
                  {field?.typeData === 'dropdown' && (
                    <div className={'col-1 px-5 align-c'}>
                      <IconUploader
                        size="small"
                        value={selection?.media_url}
                        onComplete={(e) =>
                          addMedia(e, fieldIndex, selectionIndex)
                        }
                      />
                    </div>
                  )}
                  <div className={'col-1'} style={{ paddingTop: '3px', marginLeft: field?.typeData === 'dropdown' ? '10px' : '-30px' }}>
                    {' '}
                    <IconButton
                      onClick={() => removeSelectionField(fieldIndex)}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </div>
                </div>
              </ListItem>
            );
          }
        })}

      </>
    );
  }, [
    currentLang,
    handleSelectionChange,
    addMedia,
    removeSelectionField,
    addSelectionToField,
    isDarkMode,
    t,
  ]);

  const renderFieldDetails = useCallback((field, index) => {
    if (!field) return null;

    const currentFieldType = getCurrentFieldType(field.typeData);
    const availableOptions = currentFieldType.fields || [];

    const visibleTypes = fieldTypes.slice(
      typeScrollIndex * typesPerPage,
      (typeScrollIndex + 1) * typesPerPage
    );

    return (
      <Box sx={{ pl: 4, py: 0, width: '750px', height: '72vh', maxHeight: '72vh' }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <div>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', mb: 0 }}>
              {field?.['name_' + currentLang] || t('unnamedField')}
            </Typography>
            <div>

              <Typography variant="caption" sx={{ fontWeight: 400, fontSize: '10px', color: 'text.secondary', mt: 0 }}>
                {t('id') + ': ' + field?.value || '-'}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 400, fontSize: '10px', color: 'text.secondary', mt: 0, ml: 3 }}>
                {t('otherLang') + ': ' + field?.['name_' + (currentLang === 'en' ? 'fr' : 'en')] || '-'}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 400, fontSize: '10px', color: 'text.secondary', mt: 0, ml: 3 }}>
                {t('type') + ': ' + t(field?.type + 'TYPE')}
              </Typography>
            </div>
          </div>
          <Box sx={{ display: 'flex', gap: 1 }}>

            {field?.typeData !== 'page' &&
              field?.typeData !== 'section' && !fromExtensions && (
                <Tooltip title={t('required')}>
                  <span>
                    {field?.required ? (
                      <IconButton disabled={field?.value === 'name'}
                        onClick={() => setRequired(index, false)}
                      >
                        <CheckBoxIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => setRequired(index, true)}
                      >
                        <CheckBoxOutlineBlankIcon />
                      </IconButton>
                    )}
                  </span>
                </Tooltip>
              )}
            {!fromExtensions && (
              <Tooltip title={t('publicVisibility')}>
                <span>
                  {field?.public ? (
                    <IconButton
                      onClick={() => publicField(index)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={() => publicField(index)}
                    >
                      <VisibilityOff />
                    </IconButton>
                  )}
                </span>
              </Tooltip>
            )}
            <Tooltip title={t('delete')}>
              <span>
                <IconButton
                  onClick={() => removeField(index)}
                  disabled={field?.type === 'default'}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {field?.type === 'default' && (
          <Box sx={{
            mb: 3,
            p: 1,
            bgcolor: 'info.light',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <InfoIcon color="info" />
            <Typography variant="body2" color="info.dark">
              {t('defaultFieldDescription')}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ marginBottom: '-20px', fontWeight: 600 }}>
              {t('basicInformation')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    label={t('name')}
                    value={field?.['name_' + currentLang] || ''}
                    help={'ID: ' + field?.value}
                    size="small"
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        'name_' + currentLang,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                {availableOptions.includes('help') && (
                  <Grid item xs={4}>
                    <TextField
                      label={t('helpText')}
                      value={field?.['help_' + currentLang] || ''}
                      size="small"
                      onChange={(e) =>
                        handleFieldChange(
                          index,
                          'help_' + currentLang,
                          e.target.value
                        )
                      }
                      fullWidth
                      disabled={field?.typeData === 'page' || field?.typeData === 'section'}
                    />
                  </Grid>
                )}

              </Grid>
              {/* Field Type Selection Grid */}
              <FormControl
                fullWidth
                disabled={field?.type === 'default'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  opacity: field?.type === 'default' ? 0.7 : 1,
                }}
              >
                <InputLabel
                  shrink={true}
                  required
                  sx={{
                    backgroundColor: isDarkMode
                      ? 'rgb(51,51,51)'
                      : '#FFFFFF !important',
                    padding: '2px 10px 2px 10px',
                    borderRadius: '10px',
                  }}
                >
                  {t('type')}
                </InputLabel>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    borderRadius: '10px',
                    p: 1.5,
                    bgcolor: 'background.paper',
                    minHeight: '100px',
                    height: '100px',
                    position: 'relative',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <IconButton
                      onClick={() => handleTypeScroll('left')}
                      disabled={typeScrollIndex === 0}
                      sx={{
                        position: 'absolute',
                        left: -8,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          boxShadow: '0px 4px 8px rgba(0,0,0,0.15)'
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'background.paper',
                          opacity: 0.5
                        }
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        px: 4,
                        mx: 'auto',
                        height: '75px',
                        maxWidth: 'calc(100% - 80px)',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {visibleTypes.map((type) => {
                        const Icon = type.icon;
                        const isSelected = field.typeData === type.value;
                        return (
                          <Grid item xs={1.5} key={type.value}>
                            <Box
                              onClick={() => {
                                if (field?.type !== 'default') {
                                  handleFieldChange(index, 'typeData', type.value);
                                }
                              }}
                              sx={{
                                p: 1,
                                border: isSelected ? '1px solid' : 'none',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                borderRadius: '8px',
                                cursor: field?.type === 'default' ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '75px',
                                gap: 0.5,
                                color: isSelected ? 'primary.main' : 'text.secondary',
                                bgcolor: isSelected ? businessPreference?.mainColor + '20' : 'background.paper',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  bgcolor: field?.type === 'default'
                                    ? 'background.paper'
                                    : isSelected
                                      ? businessPreference?.mainColor + '30'
                                      : 'action.hover',
                                  transform: field?.type === 'default' ? 'none' : 'translateY(-2px)',
                                  boxShadow: field?.type === 'default' ? 'none' : '0px 2px 4px rgba(0,0,0,0.1)',
                                },
                              }}
                            >
                              <Icon
                                sx={{
                                  fontSize: '24px',
                                  color: isSelected ? 'primary.main' : 'text.secondary',
                                }}
                              />
                              <Typography
                                variant="caption"
                                color={isSelected ? 'primary.main' : 'text.secondary'}
                                sx={{
                                  textAlign: 'center',
                                  fontSize: '9px',
                                  lineHeight: '10px',
                                  fontWeight: isSelected ? 600 : 400,
                                }}
                              >
                                {type.label}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <IconButton
                      onClick={() => handleTypeScroll('right')}
                      disabled={typeScrollIndex >= Math.ceil(fieldTypes.length / typesPerPage) - 1}
                      sx={{
                        position: 'absolute',
                        right: -8,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          boxShadow: '0px 4px 8px rgba(0,0,0,0.15)'
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'background.paper',
                          opacity: 0.5
                        }
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: 1.2,
                      color: '#69696960',
                      fontSize: '9px',
                      fontWeight: 500
                    }}
                  >
                    {typeScrollIndex + 1} / {Math.ceil(fieldTypes.length / typesPerPage)}
                  </Box>
                </Box>
              </FormControl>
            </Box>
          </Box>

          {fromExtensions && (
            <Box>
              <Typography variant="subtitle1" sx={{ marginBottom: '-5px', fontWeight: 600 }}>
                {t('extensionSettings')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Select
                  label={t('matchedStructureCollection')}
                  size="small"
                  noEmpty
                  value={field?.matchedStructureCollection}
                  selections={fromExtensions?.map(
                    (extension) => ({
                      id: extension,
                      value: extension,
                      label: t(extension)
                    })
                  )}
                  onChange={(e, value) =>
                    handleFieldChange(index, 'matchedStructureCollection', value)
                  }
                  fullWidth
                />
                {isExternal && (
                  <TextField
                    label={t('externalSchema')}
                    value={field?.externalSchema}
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        'externalSchema',
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                )}
              </Box>
            </Box>
          )}

          {!fromExtensions && field?.typeData !== 'page' && field?.typeData !== 'section' && (
            <Box>
              <Typography variant="subtitle1" sx={{ marginBottom: '-5px', fontWeight: 600 }}>
                {t('fieldSettings')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {availableOptions.includes('transform') && (
                  <Select
                    disabled={field?.typeData !== 'string'}
                    label={t('transform')}
                    size="small"
                    noEmpty
                    value={field?.transform || 'none'}
                    selections={[
                      { label: t('uppercase'), value: 'uppercase', id: 'uppercase' },
                      { label: t('lowercase'), value: 'lowercase', id: 'lowercase' },
                      { label: t('capitalize'), value: 'capitalize', id: 'string' },
                      { label: t('trim'), value: 'trim', id: 'trim' },
                      { label: t('none'), value: 'none', id: 'none' },
                    ]}
                    onChange={(e, value) =>
                      handleFieldChange(index, 'transform', value)
                    }
                    fullWidth
                  />
                )}

                {availableOptions.includes('expression') && (
                  <MuiTextField
                    disabled={(field?.typeData !== 'text' && field?.typeData !== 'string')}
                    label={t('regex')}
                    value={field?.validation || ''}
                    size="small"
                    onChange={(e) =>
                      handleFieldChange(
                        index,
                        'validation',
                        e.target.value
                      )
                    }
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            disabled={(field?.typeData !== 'text' && field?.typeData !== 'string')}
                            onClick={() => {
                              handleDisplayRegex(index);
                            }}
                          >
                            <SettingsSuggest />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      'MuiFormControl-root': {
                        width: '100%',
                      },
                      '.MuiInputBase-input': {
                        height: '34px',
                        padding: '2px 10px 2px 10px',
                      },
                      '& .MuiFormLabel-root': {
                        padding: '2px 10px 2px 10px',
                        borderRadius: '10px',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                      },
                    }}
                  />
                )}

                {(availableOptions.includes('prefix') || availableOptions.includes('suffix')) && (
                  <Grid container spacing={2}>
                    {availableOptions.includes('prefix') && (
                      <Grid item xs={6}>
                        <MuiTextField
                          label={t('prefix')}
                          value={field?.prefix || ''}
                          size="small"
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              'prefix',
                              e.target.value
                            )
                          }
                          fullWidth
                          disabled={field?.typeData === 'page' || field?.typeData === 'section'}
                          sx={{
                            mb: 1,
                            'MuiFormControl-root': {
                              width: '100%',
                            },
                            '.MuiInputBase-input': {
                              height: '34px',
                              padding: '2px 10px 2px 10px',
                            },
                            '& .MuiFormLabel-root': {
                              padding: '2px 10px 2px 10px',
                              borderRadius: '10px',
                            },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                            },
                          }}
                        />
                      </Grid>
                    )}
                    {availableOptions.includes('suffix') && (
                      <Grid item xs={6}>
                        <MuiTextField
                          label={t('suffix')}
                          value={field?.suffix || ''}
                          size="small"
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              'suffix',
                              e.target.value
                            )
                          }
                          fullWidth
                          disabled={field?.typeData === 'page' || field?.typeData === 'section'}
                          sx={{
                            mb: 1,
                            'MuiFormControl-root': {
                              width: '100%',
                            },
                            '.MuiInputBase-input': {
                              height: '34px',
                              padding: '2px 10px 2px 10px',
                            },
                            '& .MuiFormLabel-root': {
                              padding: '2px 10px 2px 10px',
                              borderRadius: '10px',
                            },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                            },
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}

                {/* Visibility and Groups Section */}
                {(availableOptions.includes('visibility') || availableOptions.includes('groups')) && (
                  <Box sx={{ mt: 1.4 }}>
                    <Grid container spacing={2}>
                      {availableOptions.includes('visibility') && (
                        <Grid item xs={7}>
                          <FormControl
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                                '& fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            }}
                          >
                            <InputLabel
                              shrink={true}
                              required
                              sx={{
                                backgroundColor: isDarkMode
                                  ? 'rgb(51,51,51)'
                                  : '#FFFFFF !important',
                                padding: '2px 10px 2px 10px',
                                borderRadius: '10px',
                              }}
                            >
                              {t('fieldVisibility')}
                            </InputLabel>
                            <Box sx={{
                              border: '1px solid',
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                              borderRadius: '10px',
                              p: 1,
                              maxHeight: '200px',
                              overflow: 'auto'
                            }}>
                              <RadioGroup
                                value={field?.visibility || 'both'}
                                onChange={(e) => handleVisibilityChange(index, e.target.value)}
                              >
                                <FormControlLabel
                                  value="edition"
                                  control={<Radio />}
                                  disabled={field?.value === 'name'}
                                  label={
                                    <Box>
                                      <Typography>{t('onlyEdition')}</Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0 }}>
                                        {t('onlyEditionDescription')}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  value="both"
                                  control={<Radio />}
                                  disabled={field?.value === 'name'}
                                  label={
                                    <Box>
                                      <Typography>{t('creationAndEdit')}</Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0 }}>
                                        {t('creationAndEditDescription')}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  value="hidden"
                                  control={<Radio />}
                                  disabled={field?.value === 'name'}
                                  label={
                                    <Box>
                                      <Typography>{t('hidden')}</Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0 }}>
                                        {t('hiddenDescription')}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </RadioGroup>
                            </Box>
                          </FormControl>
                        </Grid>
                      )}

                      {availableOptions.includes('groups') && (
                        <Grid item xs={5}>
                          <FormControl
                            component="fieldset"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                                '& fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            }}
                          >
                            <InputLabel
                              shrink={true}
                              required
                              sx={{
                                backgroundColor: isDarkMode
                                  ? 'rgb(51,51,51)'
                                  : '#FFFFFF !important',
                                padding: '2px 10px 2px 10px',
                                borderRadius: '10px',
                              }}
                            >
                              {t('authorizedGroups')}
                            </InputLabel>
                            <Box sx={{
                              border: '1px solid',
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                              borderRadius: '10px',
                              p: 1,
                              maxHeight: '200px',
                              overflow: 'auto'
                            }}>
                              <List dense>
                                {groupsList?.map((group) => (
                                  <ListItem key={group} disablePadding>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={field?.groups?.includes(group) || false}
                                          onChange={(e) => {
                                            const currentGroups = field?.groups || [];
                                            const newGroups = e.target.checked
                                              ? [...currentGroups, group]
                                              : currentGroups.filter(g => g !== group);
                                            handleGroupsChange(index, newGroups);
                                          }}
                                        />
                                      }
                                      label={group}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Special Options Section */}
          {availableOptions.includes('selections') && (
            <Box>

              <FormControl
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <InputLabel
                  shrink={true}
                  required
                  sx={{
                    backgroundColor: isDarkMode
                      ? 'rgb(51,51,51)'
                      : '#FFFFFF !important',
                    padding: '2px 10px 2px 10px',
                    borderRadius: '10px',
                  }}
                >
                  {t('selectionOptions')}
                </InputLabel>
                <Box sx={{
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: '10px',
                  p: 1,
                  position: 'relative'
                }}>
                  <IconButton
                    size="small"
                    onClick={() => addSelectionToField(index)}
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -3,
                      zIndex: 10,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '100px',
                      width: '32px',
                      height: '32px',
                      '&:hover': {
                        bgcolor: 'background.paper',
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                        color: 'text.secondary'
                      }
                    }}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                  {renderSelectionManagement(field, index)}
                </Box>
              </FormControl>
            </Box>
          )}

          {/* Conditions Section */}
          {availableOptions.includes('conditions') && !fromExtensions && (
            <Box>

              <FormControl
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <InputLabel
                  shrink={true}
                  required
                  sx={{
                    backgroundColor: isDarkMode
                      ? 'rgb(51,51,51)'
                      : '#FFFFFF !important',
                    padding: '2px 10px 2px 10px',
                    borderRadius: '10px',
                  }}
                >
                  {t('conditions')}
                </InputLabel>
                <Box sx={{
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                  borderRadius: '10px',
                  p: 1,
                  position: 'relative'
                }}>
                  <IconButton
                    size="small"
                    onClick={addCondition}
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -3,
                      zIndex: 10,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '100px',
                      width: '32px',
                      height: '32px',
                      '&:hover': {
                        bgcolor: 'background.paper',
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                        color: 'text.secondary'
                      }
                    }}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                  <List>
                    {(field?.conditional || []).length === 0 ? (
                      <ListItem>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {t('noConditions')}
                        </Typography>
                      </ListItem>
                    ) : (
                      (field?.conditional || []).map(
                        (condition, idx) => (
                          <React.Fragment key={idx}>
                            <ListItem>
                              <div className="col-4">
                                <Select
                                  label={t('field')}
                                  staticView
                                  key={idx + 'field-cond'}
                                  selections={fields?.map((field) => ({
                                    value: field?.value,
                                    id: field?.value,
                                    label: field?.name,
                                  }))}
                                  value={condition?.field}
                                  onChange={(e, value) =>
                                    handleConditionChange(idx, 'field', value)
                                  }
                                  fullWidth
                                  noEmpty
                                />
                              </div>
                              <div className="col-3 px-2">
                                <Select
                                  variant="outlined"
                                  label={t('operator')}
                                  selections={[
                                    {
                                      label: t('equal'),
                                      value: '==',
                                      id: '==',
                                    },
                                    {
                                      label: t('notEqual'),
                                      value: '!=',
                                      id: '!=',
                                    },
                                    {
                                      label: t('greaterThan'),
                                      value: '>',
                                      id: '>',
                                    },
                                    {
                                      label: t('lessThan'),
                                      value: '<',
                                      id: '<',
                                    },
                                    {
                                      label: t('includes'),
                                      value: 'includes',
                                      id: 'includes',
                                    },
                                  ]}
                                  noEmpty
                                  value={condition?.operator || ''}
                                  onChange={(e, value) =>
                                    handleConditionChange(idx, 'operator', value)
                                  }
                                  fullWidth
                                />
                              </div>
                              <div className="col-4 mt-2">
                                {(() => {
                                  const selectedField = fields?.find(f => f.value === condition.field);
                                  if (!selectedField) return null;

                                  switch (selectedField.typeData) {
                                    case 'multi-select':
                                    case 'dropdown':
                                    case 'status':
                                      return (
                                        <Select
                                          label={t('value')}
                                          selections={selectedField.selections?.map(sel => ({
                                            value: sel.value,
                                            id: sel.value,
                                            label: sel[`label_${currentLang}`],
                                          })) || []}
                                          value={condition.value || ''}
                                          onChange={(e, value) => handleConditionChange(idx, 'value', value)}
                                          fullWidth
                                        />
                                      );
                                    case 'selection':
                                      return (
                                        <Select
                                          label={t('value')}
                                          selections={selectedField.selections?.map(sel => ({
                                            value: sel?.[`label_${currentLang}`],
                                            id: sel.value,
                                            label: sel?.[`label_${currentLang}`],
                                          })) || []}
                                          value={condition.value || ''}
                                          onChange={(e, value) => handleConditionChange(idx, 'value', value)}
                                          fullWidth
                                        />
                                      );
                                    case 'number':
                                      return (
                                        <MuiTextField
                                          type="number"
                                          label={t('value')}
                                          value={condition.value || ''}
                                          onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                                          fullWidth
                                          sx={{
                                            'MuiFormControl-root': { width: '100%' },
                                            '.MuiInputBase-input': {
                                              height: '48px',
                                              padding: '2px 10px 2px 10px',
                                            },
                                            '& .MuiFormLabel-root': {
                                              padding: '2px 10px 2px 10px',
                                              borderRadius: '10px',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                              borderRadius: '10px',
                                            },
                                          }}
                                        />
                                      );
                                    case 'boolean':
                                      return (
                                        <Select
                                          label={t('value')}
                                          selections={[
                                            { value: 'true', id: 'true', label: t('yes') },
                                            { value: 'false', id: 'false', label: t('no') },
                                          ]}
                                          value={condition.value || ''}
                                          onChange={(e, value) => handleConditionChange(idx, 'value', value)}
                                          fullWidth
                                        />
                                      );
                                    case 'date':
                                    case 'date-time':
                                      return (
                                        <MuiTextField
                                          type="datetime-local"
                                          label={t('value')}
                                          value={condition.value || ''}
                                          onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                                          fullWidth
                                          InputLabelProps={{ shrink: true }}
                                          sx={{
                                            'MuiFormControl-root': { width: '100%' },
                                            '.MuiInputBase-input': {
                                              height: '48px',
                                              padding: '2px 10px 2px 10px',
                                            },
                                            '& .MuiFormLabel-root': {
                                              padding: '2px 10px 2px 10px',
                                              borderRadius: '10px',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                              borderRadius: '10px',
                                            },
                                          }}
                                        />
                                      );
                                    default:
                                      return (
                                        <MuiTextField
                                          label={t('value')}
                                          value={condition.value || ''}
                                          onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                                          fullWidth
                                          sx={{
                                            'MuiFormControl-root': { width: '100%' },
                                            '.MuiInputBase-input': {
                                              height: '48px',
                                              padding: '2px 10px 2px 10px',
                                            },
                                            '& .MuiFormLabel-root': {
                                              padding: '2px 10px 2px 10px',
                                              borderRadius: '10px',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                              borderRadius: '10px',
                                            },
                                          }}
                                        />
                                      );
                                  }
                                })()}
                              </div>
                              <div className="col-1">
                                <IconButton onClick={() => removeCondition(idx)}>
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            </ListItem>
                            {idx < field?.conditional.length - 1 && (
                              <ListItem>
                                <div
                                  className="row"
                                  style={{
                                    marginTop: '-15px',
                                    marginBottom: '-15px',
                                    marginLeft: '100px',
                                  }}
                                >
                                  <Selection
                                    label={t('logic')}
                                    value={condition.logic}
                                    onChange={(valueF, value) =>
                                      handleConditionChange(idx, 'logic', value)
                                    }
                                    field={{
                                      typeData: 'selectionNode',
                                    }}
                                    selections={[
                                      {
                                        label: t('AND'),
                                        value: 'AND',
                                        id: 'AND',
                                        color: '#000000',
                                      },
                                      {
                                        label: t('OR'),
                                        value: 'OR',
                                        id: 'OR',
                                        color: '#000000',
                                      },
                                    ]}
                                    noForm
                                  />
                                </div>
                              </ListItem>
                            )}
                          </React.Fragment>
                        )
                      )
                    )}
                  </List>
                </Box>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>
    );
  }, [
    currentLang,
    handleFieldChange,
    handleDisplayRegex,
    publicField,
    removeField,
    setRequired,
    t,
    fromExtensions,
    isExternal,
    renderSelectionManagement,
    handleConditionChange,
    removeCondition,
    addCondition,
    fields,
    groupsList,
    handleGroupsChange,
    typeScrollIndex,
    businessPreference,
    isDarkMode,
    getCurrentFieldType,
  ]);

  return (
    <Box sx={{ display: 'flex', height: '72vh', width: '100%' }}>
      <Paper
        sx={{
          width: '300px',
          height: '72vh',
          overflow: 'hidden',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
        elevation={0}
      >

        <Paper
          sx={{
            height: '68vh',
            overflow: 'scroll',
          }}
          elevation={0}
        >
          <List>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="fields">
                {(provided) => (
                  <Box {...provided.droppableProps} ref={provided.innerRef}>
                    {fields?.map((field, index) => (
                      <Draggable
                        key={index + 'field-drop'}
                        draggableId={field?.id ? field.id + index : `field-${index}`}
                        index={index}
                      >
                        {(provided) => (
                          <ListItemButton
                            dense
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            selected={selectedFieldIndex === index}
                            onClick={() => handleFieldSelect(index)}
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              borderRadius: '8px',

                            }}
                          >
                            <ListItemIcon>
                              <DragHandleOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={field?.['name_' + currentLang] || t('unnamedField')}
                              secondary={t('type') + ': ' + t(field?.typeData)}
                              primaryTypographyProps={{
                                fontSize: '12px',
                                fontWeight: selectedFieldIndex === index ? 700 : 500,
                              }}
                              secondaryTypographyProps={{
                                fontSize: '10px',
                                fontWeight: selectedFieldIndex === index ? 600 : 400,
                              }}

                            />
                          </ListItemButton>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </List>
        </Paper>
        <div className="align-c" style={{ paddingRight: '10px' }}>
          <Button
            startIcon={<AddCircleOutlineIcon size='small' sx={{ fontSize: '14px' }} />}
            onClick={addField}
            sx={{ m: 0, p: 1, textTransform: 'none', width: '100%', borderRadius: '8px', backgroundColor: '#69696910', border: 'none', boxShadow: 'none', color: '#000000' }}
          >
            {t('addField')}
          </Button>
        </div>
      </Paper>
      <Paper
        sx={{
          height: '72vh',
          overflow: 'scroll',
        }}
        elevation={0}
      >
        {selectedFieldIndex !== null ? (
          renderFieldDetails(fields[selectedFieldIndex], selectedFieldIndex)
        ) : (
          <div className="align-c" style={{ height: '72vh', width: '750px' }}>
            <Blocks heightPercentage={80} empty={true} emptyType={'select'} emptyMessage={t('selectAField')} />
          </div>
        )}
      </Paper>
      <Menu
        anchorEl={displayRegexMenu}
        open={Boolean(displayRegexMenu)}
        onClose={() => setDisplayRegexMenu(false)}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={{
          '.MuiMenu-paper': {
            width: '300px',
          },
        }}
        PaperProps={{
          sx: {
            width: '300px',
            elevation: 1,
            boxShadow: '0px 0px 0px 0px',
          },
        }}
      >
        <GeneralText
          text={t('regex')}
          fontSize="14px"
          size="bold"
          primary={true}
          classNameComponent="p-4"
          beta
        />
        <div className="row px-3">
          <MenuItem
            key={currentRegexIndex + 'regex-email'}
            onClick={() => {
              handleSelectRegex(
                currentRegexIndex,
                '/^(([^<>()[]\\.,;:s@"]+(.[^<>()[]\\.,;:s@"]+)*)|(".+"))@(([[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}])|(([a-zA-Z-0-9]+.)+[a-zA-Z]{2,}))$/'
              );
            }}
          >
            {t('email')}
          </MenuItem>
          <MenuItem
            key={currentRegexIndex + 'regex-phone'}
            onClick={() => {
              handleSelectRegex(
                currentRegexIndex,
                '/^+?d{1,4}?[-. ]?(?(?:d{1,3}?)?[-. ]?)?(?:d{1,4}[-. ]?){2}d{1,9}$/'
              );
            }}
          >
            {t('phone')}
          </MenuItem>
          <MenuItem
            key={currentRegexIndex + 'regex-url'}
            onClick={() => {
              handleSelectRegex(
                currentRegexIndex,
                '/^(http|https)://[^ "]+$'
              );
            }}
          >
            {t('url')}
          </MenuItem>
          <MenuItem
            key={currentRegexIndex + 'regex-number'}
            onClick={() => {
              handleSelectRegex(
                currentRegexIndex,
                '/^[0-9]*$/'
              );
            }}
          >
            {t('numberOnly')}
          </MenuItem>
          <MenuItem
            key={currentRegexIndex + 'regex-alphanumeric'}
            onClick={() => {
              handleSelectRegex(
                currentRegexIndex,
                '/^[a-zA-Z0-9]*$/'
              );
            }}
          >
            {t('alphanumeric')}
          </MenuItem>
          <MenuItem
            key={currentRegexIndex + 'regex-address'}
            onClick={() => {
              handleSelectRegex(
                currentRegexIndex,
                '/^[a-zA-Z0-9 .,]*$/'
              );
            }}
          >
            {t('address')}
          </MenuItem>
          <MenuItem
            key={currentRegexIndex + 'regex-oneLetter-oneNumber'}
            onClick={() => {
              handleSelectRegex(
                currentRegexIndex,
                '/^(?=.*[A-Za-z])(?=.*d)[A-Za-zd]{8,}$/'
              );
            }}
          >
            {t('oneLetter-oneNumber')}
          </MenuItem>
        </div>
      </Menu>
    </Box>
  );
};

export default FieldEditor;
